import { useLocation } from "react-router-dom";

import AddAdmin from "../Admin/AddAdmin";
import AddNurse from "../Nurse/AddNurse";
import AddSurgeon from "../Surgeon/AddSurgeon";
import AddCourier from "../Courier/AddCourier";

/** Supported user type values for add-user routing. */
type SupportedUserType =
  | "NURSE"
  | "NURSES"
  | "ADMIN"
  | "ADMINS"
  | "DOCTOR"
  | "SURGEONS"
  | "COURIER"
  | "COURIERS";

/** Route state shape for add-user navigation. */
type AddUserState = {
  userType?: SupportedUserType;
};

/**
 * Type guard for the add-user route state.
 *
 * Args:
 *   value: Unknown location state to validate.
 *
 * Returns:
 *   boolean: True when the state matches the expected shape.
 */
const isAddUserState = (value: unknown): value is AddUserState => {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const maybeState = value as { userType?: unknown };
  const userType = maybeState.userType;
  return (
    userType === undefined ||
    userType === "NURSE" ||
    userType === "NURSES" ||
    userType === "ADMIN" ||
    userType === "ADMINS" ||
    userType === "DOCTOR" ||
    userType === "SURGEONS" ||
    userType === "COURIER" ||
    userType === "COURIERS"
  );
};

/**
 * Routes to the correct add form based on the selected user tab.
 *
 * Returns:
 *   JSX.Element: The add user form for the resolved user type.
 *
 * Notes:
 *   Defaults to the nurse form when the route state is missing or invalid.
 */
const AddUserTab = () => {
  const location = useLocation();
  const state = isAddUserState(location.state) ? location.state : null;
  const userType = state?.userType;

  if (userType === "ADMIN" || userType === "ADMINS") {
    return <AddAdmin />;
  }

  if (userType === "DOCTOR" || userType === "SURGEONS") {
    return <AddSurgeon />;
  }

  if (userType === "COURIER" || userType === "COURIERS") {
    return <AddCourier />;
  }

  return <AddNurse />;
};

export default AddUserTab;
